'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Case, Default, Switch } from 'react-if';

import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';

import { gql, useMutation } from '@apollo/client';
import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { getLogger } from '@lib/logging';
import Paths from '@lib/paths';
import {
  CREATE_TRUST_SESSION_ID_MUTATION,
  TU_AUTHENTICATION_STATUS_PROFILE_QUERY,
} from '@components/transunion-flow/steps/queries';
import {
  CURRENT_TU_TERMS_OF_SERVICE_VERSION,
  TU_TERMS_OF_SERVICE_TYPE,
} from '@components/transunion-flow/steps/constants';
import {
  TuStep,
  useTuStepper,
} from '@components/transunion-flow/tu-modal-stepper';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';
import {
  hasOtpAuthenticationQuestions,
  hasOtpPasscodeQuestions,
  isActive,
  isTUBlocked,
} from '@components/transunion-flow/utils';
import ErrorBanner from '@components/error-banner/error-banner';
import iovationLoader from './iovation-loader.js';
import styles from './initial.module.scss';

const logger = getLogger('InitialStep');

export const CREDIT_SCORE_USER_INFO_QUERY = gql`
  query GetCreditScoreUserInfo {
    globalIdentityProfile {
      firstName
      lastName
      mailingAddress {
        city
        state
        addressLine1
        addressLine2
        zipCode
      }
      phoneNumber
      dateOfBirth
    }
  }
`;

export const TOS_ACKNOWLEDGEMENT_QUERY = gql`
  query GetTUTermsOfServiceAcknowledgements {
    termsOfServiceIdentityProfile {
      acknowledgements {
        type
        source
        version
      }
    }
  }
`;

const setIovationConfiguration = (blackBoxCollectionCallback) => {
  if (typeof window !== 'undefined') {
    if (blackBoxCollectionCallback) {
      window.io_global_object_name = 'IGLOO';
      window.IGLOO = {
        enable_flash: false,
        enable_atrip: false,
        bb_callback: blackBoxCollectionCallback,
        loader: {
          uri_hook: '/iojs',
          version: 'general5',
        },
      };
    } else {
      // cleanup
      delete window.io_global_object_name;
      delete window.IGLOO;
    }
  }
};

/**
 * This is the first step of the TU flow which fetches all necessary info to start with
 */
const InitialStep = () => {
  const {
    data: userInfoData,
    error: userInfoError,
    loading: userInfoLoading,
  } = useQueryWithLog(logger, CREDIT_SCORE_USER_INFO_QUERY);
  const {
    data: authStatusProfileData,
    error: authStatusProfileError,
    loading: authStatusProfileLoading,
  } = useQueryWithLog(logger, TU_AUTHENTICATION_STATUS_PROFILE_QUERY, {
    fetchPolicy: 'network-only',
  });
  const {
    data: tosProfileData,
    error: tosProfileError,
    loading: tosProfileLoading,
  } = useQueryWithLog(logger, TOS_ACKNOWLEDGEMENT_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [createTrustSessionId] = useMutation(CREATE_TRUST_SESSION_ID_MUTATION);
  const [createdTrustSession, setCreatedTrustSession] = useState(false);
  const blackBoxCollectionCompletedRef = useRef(false);

  const { setTuVisibility } = useTuVisibilityContext();
  const { setTuCustomerInfo, setAuthStatusProfile, setTosAcknowledged } =
    useTuStepper();

  const blackBoxCollectionCallback = useCallback(
    async (blackBox, complete) => {
      if (!blackBoxCollectionCompletedRef.current && complete) {
        // transitioned to a complete state
        blackBoxCollectionCompletedRef.current = true;
        // we can now create the Trust Session ID with the collected blackBox
        await createTrustSessionId({
          variables: {
            blackBox,
          },
        }).then(() => {
          setCreatedTrustSession(true);
        });
      }
    },
    [createTrustSessionId],
  );

  useEffect(() => {
    if (!blackBoxCollectionCompletedRef.current) {
      setIovationConfiguration(blackBoxCollectionCallback);
      // Config must be set before loading the following script to indicate
      // where to fetch additional scripts via the iojs proxy.
      iovationLoader();
      // the blackBoxCollectionCallback method will be called as the collection occurs
    }
    return () => {
      setIovationConfiguration(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    // Once data has been fetched, determine the next state to transition to.
    // It's possible for the Auth Status Profile and Terms of Service Profile to not,
    // which is still a valid state to move forward with.
    if (
      createdTrustSession &&
      userInfoData?.globalIdentityProfile &&
      (authStatusProfileData || authStatusProfileError) &&
      (tosProfileData || tosProfileError)
    ) {
      // TU terms of agreement for a specific type/version can only be stored once.  Check if a user already acknowledged it.
      const acknowledgements =
        tosProfileData?.termsOfServiceIdentityProfile?.acknowledgements ?? [];
      const tosAcknowledged = acknowledgements.some(
        (ack) =>
          ack.type === TU_TERMS_OF_SERVICE_TYPE &&
          ack.version === CURRENT_TU_TERMS_OF_SERVICE_VERSION,
      );
      setTosAcknowledged(tosAcknowledged);

      const authStatusProfile =
        authStatusProfileData?.tuAuthenticationStatusProfile;

      if (authStatusProfile) {
        setAuthStatusProfile(authStatusProfile);

        if (isActive(authStatusProfile)) {
          // Should not be starting up the flow if a user already has an active TU profile.
          // Automatically jump to the success state to close out the flow.
          setTuVisibility({
            show: true,
            tuStep: TuStep.TU_SUCCESS,
          });
          return;
        } else if (isTUBlocked(authStatusProfile)) {
          // If a user is currently blocked, should route to the credit score page which
          // will display a blocked message
          window.location.href = Paths.DASHBOARD_CREDIT_SCORE;
          return;
        } else if (authStatusProfile?.questions) {
          if (hasOtpPasscodeQuestions(authStatusProfile)) {
            // Start up at the OTP Passcode screen if a user was already prompted to input a passcode.
            setTuVisibility({
              show: true,
              tuStep: TuStep.TU_OTP_PASSCODE_ENTRY,
            });
            return;
          }
          if (!hasOtpAuthenticationQuestions(authStatusProfile)) {
            // If a user was already in the middle of activating TU and has
            // KBA verification questions to answer, transition to the corresponding screen
            setTuVisibility({
              show: true,
              tuStep: TuStep.TU_VERIFICATION_QUESTIONS,
            });
            return;
          }
        }
      }
      // Pre-fill user info data
      const userInfo = userInfoData.globalIdentityProfile;
      setTuCustomerInfo({
        firstName: userInfo.firstName ?? '',
        lastName: userInfo.lastName ?? '',
        mailingAddress: {
          addressLine1: userInfo.mailingAddress?.addressLine1 ?? '',
          addressLine2: userInfo.mailingAddress?.addressLine2 ?? '',
          city: userInfo.mailingAddress?.city ?? '',
          state: userInfo.mailingAddress?.state ?? '',
          zipCode: userInfo.mailingAddress?.zipCode ?? '',
        },
        phoneNumber: userInfo.phoneNumber ?? '',
        dateOfBirth: userInfo.dateOfBirth ?? '',
      });
      // Start from the beginning form
      setTuVisibility({
        show: true,
        tuStep: TuStep.TU_BASIC_INFO,
      });
    }
  }, [
    createdTrustSession,
    userInfoData,
    authStatusProfileData,
    authStatusProfileError,
    tosProfileData,
    tosProfileError,
    setTuCustomerInfo,
    setAuthStatusProfile,
    setTosAcknowledged,
    setTuVisibility,
  ]);

  const isLoading =
    userInfoLoading || authStatusProfileLoading || tosProfileLoading;
  const hasError = !!userInfoError;

  return (
    <div className={styles.container}>
      <Switch>
        <Case condition={!isLoading && hasError}>
          <ErrorBanner errorMessage="Looks like something went wrong. Try again later." />
        </Case>
        <Default>
          <LoadingSpinner />
        </Default>
      </Switch>
    </div>
  );
};

export default InitialStep;
